import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik, type FormikProps } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import FormControlLabel from '~/components/core/Atomic/FormControlLabel';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Tooltip from '~/components/core/Atomic/Tooltip';
import DialogFooterActionsUntyped from '~/components/core/DialogFooterActions';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import Text from '~/components/core/TextComponents/Text';
import {
  CLAIM_CLOSING_REASON_INITIAL_VALUES,
  ClaimClosingReasonFields,
  type ClaimClosingReasonValues,
  getClaimClosingReasonValidationSchema,
  useClaimClosingReasons,
} from '~/components/exposures/ClaimClosingReasonFormik';
import { CONFIGURABLE_FNOL_POLICY_TYPES } from '~/components/Fnol/createLikeDraftUtils';
import { useClaimCloseFlow } from '~/components/hooks/useClaimCloseFlow';
import { useCms } from '~/components/hooks/useCms';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import type { LegalActionApiResponse } from '~/components/LegalActions/types';
import Checkbox from '~/components/ui/atoms/Checkbox';
import Radio from '~/components/ui/atoms/Radio';
import RadioGroup from '~/components/ui/atoms/RadioGroup';
import useDataFetcher from '~/components/useDataFetcher';
import type { ExternalClaimNumberList } from '~/server_shared/openapi/data-contracts';
import type { ConfiguredField, PolicyForFe } from '~/server_shared/swagger/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, isPolicyManuallyFilled, isPolicyNotFound } from '~/Utils';
import type { ClaimForClose } from '~/Utils/closeClaimUtils';

type CreateLikeSourceClaim = ClaimForClose & {
  claim_id_display: string;
  is_closed?: boolean;
  incident: { date_of_loss?: string | null };
  policy: Pick<PolicyForFe, 'id' | 'type' | 'is_manually_created' | 'is_manually_filled'>;
};

interface CreateLikeClaimDialogProps {
  claim: CreateLikeSourceClaim;
  onClose: () => void;
}

// DialogFooterActions is a .jsx file; under allowJs, `primaryTooltip` is inferred as the literal
// `undefined` type because its JS default is `undefined`. Cast to the real shape it accepts.
interface DialogFooterActionsProps {
  primaryLabel?: string;
  disabled?: boolean;
  primaryDisabled?: boolean;
  primaryTooltip?: string;
  onClickPrimary?: () => void;
  onClickSecondary?: () => void;
}
const DialogFooterActions = DialogFooterActionsUntyped as React.ComponentType<DialogFooterActionsProps>;

const NO_CLOSE_REASONS_MESSAGE = 'No closing reason is available for this claim';

// The partition the backend copies by: exposure-level legal actions hang off exposures the new
// claim does not have, and closed ones stay on the source claim.
const isCarriableLegalAction = (legalAction: LegalActionApiResponse) =>
  legalAction.is_claim_level && legalAction.is_open;

// "1 closed legal action stays" / "2 closed legal actions stay": the wording agrees in number with
// the count, as the backend's note does.
const staysOnClaim = (count: number, kind: string, claimIdDisplay: string) =>
  `${count} ${kind} legal action${count === 1 ? '' : 's'} ${count === 1 ? 'stays' : 'stay'} on claim ${claimIdDisplay}`;

// Which of the source claim's legal actions the copy leaves behind, and why; null when it carries
// them all.
const legalActionsNotCopiedMessage = (legalActions: LegalActionApiResponse[], claimIdDisplay: string) => {
  const notCopiedCount = legalActions.filter((legalAction) => !isCarriableLegalAction(legalAction)).length;
  if (!notCopiedCount) {
    return null;
  }
  const exposureLevelCount = legalActions.filter((legalAction) => !legalAction.is_claim_level).length;
  const closedCount = legalActions.filter((legalAction) => legalAction.is_claim_level && !legalAction.is_open).length;
  const clauses = [
    ...(exposureLevelCount
      ? [`${staysOnClaim(exposureLevelCount, 'exposure-level', claimIdDisplay)}, because exposures are not copied`]
      : []),
    ...(closedCount ? [staysOnClaim(closedCount, 'closed', claimIdDisplay)] : []),
  ].join('; ');
  if (notCopiedCount < legalActions.length) {
    return `${notCopiedCount} of ${legalActions.length} legal actions cannot be copied: ${clauses}.`;
  }
  if (legalActions.length === 1) {
    return `This claim's only legal action cannot be copied: ${clauses}.`;
  }
  return `None of this claim's ${legalActions.length} legal actions can be copied: ${clauses}.`;
};

// A manual policy is never reused: the policy search leaves manual policies out of every result,
// so reusing one would hand the new claim a policy the search could not have picked. Nor is one of a
// type configurable FNOL has no form for, which FNOL would only turn back to the search.
const getExistingPolicyDisabledReason = (claim: CreateLikeSourceClaim) => {
  if (isPolicyNotFound(claim)) {
    return `Claim ${claim.claim_id_display} was reported without a policy (Policy Not Found), so there is no policy to reuse`;
  }
  if (isPolicyManuallyFilled(claim.policy)) {
    return `The policy of claim ${claim.claim_id_display} was filled in manually, and a manually filled policy cannot be reused`;
  }
  if (!claim.policy.type || !CONFIGURABLE_FNOL_POLICY_TYPES.includes(claim.policy.type)) {
    return `The policy of claim ${claim.claim_id_display} is on a line of business the new claim form does not support, so it cannot be reused`;
  }
  return '';
};

const CreateLikeClaimDialog: React.FC<CreateLikeClaimDialogProps> = ({ claim, onClose }) => {
  const history = useHistory();
  const { userOrganization } = useCms();
  const [shouldCloseOriginal, setShouldCloseOriginal] = useState(true);
  const [copyDocuments, setCopyDocuments] = useState(true);
  const [copyNotes, setCopyNotes] = useState(true);
  const [copyCommunications, setCopyCommunications] = useState(true);
  const [copyContacts, setCopyContacts] = useState(true);
  const [copyExternalClaimNumbers, setCopyExternalClaimNumbers] = useState(true);
  const [copyLegalActions, setCopyLegalActions] = useState(true);
  const [useExistingPolicy, setUseExistingPolicy] = useState(false);
  const [selectedCloseTypeKey, setSelectedCloseTypeKey] = useState('');

  const existingPolicyDisabledReason = getExistingPolicyDisabledReason(claim);
  const canUseExistingPolicy = !existingPolicyDisabledReason;
  const isExistingPolicySelected = useExistingPolicy && canUseExistingPolicy;

  // The checkbox is only offered when there is something to copy; the numbers are not on the
  // claim-page claim object, so they are fetched the same way the External Claim Numbers card
  // fetches them. The route is permission-gated, so users who cannot read it are not sent to it at
  // all, and a failure of the fetch itself must not put a modal in front of someone who opened
  // this dialog for something else — the checkbox simply is not offered.
  const hasExternalClaimNumberReadPermission = useHasPermission({
    action: PERMISSION_ACTIONS.EXTERNAL_CLAIM_NUMBER,
    verb: PERMISSION_VERBS.READ,
  });
  const { data: externalClaimNumbers = [] } = useDataFetcher<ExternalClaimNumberList>(
    `/api/v1/external_claim_number/${claim.id}`,
    null,
    { shouldFetch: hasExternalClaimNumberReadPermission, shouldOpenErrorDialog: false }
  );
  const hasExternalClaimNumbers = externalClaimNumbers.length > 0;

  // Fetched and offered the same way as the external claim numbers, but gated on everything the
  // backend checks before it accepts the copy: the feature and both LEGAL_ACTIONS verbs.
  const isLegalActionsFeatureEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.LEGAL_ACTIONS);
  const hasLegalActionsReadPermission = useHasPermission({
    action: PERMISSION_ACTIONS.LEGAL_ACTIONS,
    verb: PERMISSION_VERBS.READ,
  });
  const hasLegalActionsWritePermission = useHasPermission({
    action: PERMISSION_ACTIONS.LEGAL_ACTIONS,
    verb: PERMISSION_VERBS.WRITE,
  });
  const canCopyLegalActions =
    isLegalActionsFeatureEnabled && hasLegalActionsReadPermission && hasLegalActionsWritePermission;
  // `undefined` until the list lands, and it stays so when the fetch is skipped or fails.
  const { data: legalActions } = useDataFetcher<LegalActionApiResponse[]>(
    `/api/v1/claims/${claim.id}/legal_actions`,
    null,
    { shouldFetch: canCopyLegalActions, shouldOpenErrorDialog: false }
  );
  const hasCarriableLegalActions = !!legalActions?.some(isCarriableLegalAction);
  const hasLandedWithNothingToCopy = !!legalActions && !hasCarriableLegalActions;
  const legalActionsNotCopied =
    canCopyLegalActions && legalActions ? legalActionsNotCopiedMessage(legalActions, claim.claim_id_display) : null;

  const navigateToFnol = () => {
    onClose();
    history.push('/fnol', {
      createLikeContext: {
        sourceClaimId: claim.id,
        sourceClaimIdDisplay: claim.claim_id_display,
        // Seeds the policy search's date-of-loss input, so the search finds the policy that was
        // in force at the actual loss date without re-typing it. A source claim without a loss
        // date contributes no key at all, rather than an `undefined` one.
        ...(claim.incident.date_of_loss ? { sourceDateOfLoss: claim.incident.date_of_loss } : {}),
        copyDocuments,
        copyNotes,
        copyCommunications,
        copyContacts,
        // Not conjoined with `hasExternalClaimNumbers`: Continue is clickable before the fetch
        // lands (and the fetch can be skipped or fail outright), and the backend emits the key
        // only when the source actually has numbers — so the unchecked box, not the absent
        // answer, is what suppresses the carry.
        copyExternalClaimNumbers,
        // Conjoined with the gates, unlike the external claim numbers above: a `true` with no numbers
        // to carry is harmless, but the backend refuses the whole FNOL over a `copy_legal_actions`
        // it may not act on (400 without the feature, 403 without LEGAL_ACTIONS READ or WRITE).
        // Before the list lands, or when its fetch fails, the ticked default stands and the backend
        // copies whatever is carriable; a list that has landed with nothing carriable offered no
        // checkbox, so nothing was ticked.
        copyLegalActions: canCopyLegalActions && copyLegalActions && !hasLandedWithNothingToCopy,
        // FNOL skips the policy search and reports under this policy. Absent rather than `false`
        // when unticked, so the search flow's route state is exactly what it was.
        ...(isExistingPolicySelected ? { useExistingPolicy: true, sourcePolicyId: claim.policy.id } : {}),
      },
    });
  };

  const {
    closeTypes: orgCloseTypes,
    closeDisabledReasons: orgCloseDisabledReasons,
    isConfigurableClosingReasonEnabled,
    isClosing,
    isCloseDialogOpen,
    startClose,
    submitConfigurableClose,
    closeDialogs,
  } = useClaimCloseFlow({
    claim,
    onClosed: navigateToFnol,
  });

  // The configurable closing reason is collected in place, so its inputs — and the server-side
  // requirements gate the shared close dialog used to run — are needed up front.
  const closingReasons = useClaimClosingReasons(claim);
  // The route marshals a list of unanswered fields; flask-restx declares the item model, so the
  // array-ness is not in the generated contract.
  const { data: mandatoryUnansweredCloseFields } = useDataFetcher<ConfiguredField[]>(
    `/api/v1/claims/${claim.id}/close_requirements`
  );
  const configurableCloseFormRef = useRef<FormikProps<ClaimClosingReasonValues>>(null);

  const isSourceClosed = !!claim.is_closed;
  const isSubmitting = isClosing;

  // `delete_claim` soft-deletes the source claim, which the new claim is about to be linked to, so
  // it is not offered here. It is also the only close type carrying a `confirm_pop_up`, which this
  // dialog's select — unlike the claim page's WithConfirm-wrapped menu — has no place to show.
  const closeTypes = orgCloseTypes.filter((closeType) => !closeType.confirm_pop_up);

  const getCloseDisabledReasons = () => {
    if (isSourceClosed) {
      return [];
    }
    const reasons = [...orgCloseDisabledReasons];
    if (!isConfigurableClosingReasonEnabled && !closeTypes.length) {
      reasons.push(NO_CLOSE_REASONS_MESSAGE);
    }
    if (isConfigurableClosingReasonEnabled && mandatoryUnansweredCloseFields?.length) {
      // The shared close dialog surfaced these in a modal after Continue; collected in place,
      // they read better as a close blocker on the choice itself.
      reasons.push(
        `The following details must be filled to close the claim: ${mandatoryUnansweredCloseFields
          .map((field) => field.desc)
          .join(', ')}`
      );
    }
    return reasons;
  };

  const closeDisabledReasons = getCloseDisabledReasons();
  const canCloseOriginal = !closeDisabledReasons.length;
  const isCloseOutSelected = shouldCloseOriginal && canCloseOriginal;

  const selectedCloseType = closeTypes.find((closeType) => closeType.key === selectedCloseTypeKey);

  // The closing reason is chosen in place, below the close radio, for both reason models: the
  // legacy close-types select, or the configurable reason form (reason + note + notifications).
  const isCloseAndContinue = !isSourceClosed && isCloseOutSelected;
  const isLegacyInlineReasonShown = isCloseAndContinue && !isConfigurableClosingReasonEnabled;
  const isConfigurableInlineReasonShown = isCloseAndContinue && isConfigurableClosingReasonEnabled;

  const handleContinue = () => {
    if (!isCloseAndContinue) {
      navigateToFnol();
      return;
    }
    if (isConfigurableClosingReasonEnabled) {
      // Validates the inline form (surfacing its own field errors) and posts when valid.
      void configurableCloseFormRef.current?.submitForm();
      return;
    }
    if (selectedCloseType) {
      void startClose(selectedCloseType);
    }
  };

  // The shared flow's dialogs take over while open: the configurable closing reasons for orgs
  // with the feature, and the exposure closing reason for financial-only orgs. Dismissing one
  // falls back to this dialog.
  if (isCloseDialogOpen) {
    return <>{closeDialogs}</>;
  }

  return (
    <CardDialog
      isDialog
      open
      fullWidth
      maxWidth="sm"
      title="Create Like Claim"
      onClose={onClose}
      preventClose={isSubmitting}
      footerActions={
        <DialogFooterActions
          primaryLabel={isCloseAndContinue ? 'Close Claim & Continue' : 'Continue'}
          disabled={isSubmitting}
          primaryDisabled={isLegacyInlineReasonShown && !selectedCloseType}
          primaryTooltip={
            isLegacyInlineReasonShown && !selectedCloseType ? 'Choose a closing reason to continue' : undefined
          }
          onClickPrimary={handleContinue}
          onClickSecondary={onClose}
        />
      }
    >
      <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.REGULAR}>
        {isExistingPolicySelected ? (
          <>
            A new claim will be created based on claim {claim.claim_id_display} under the same policy — so on the same
            line of business and sub-organization — and both claims will be linked to each other. The next screen is the
            pre-filled claim form, with no policy search. To create the claim under a different line of business or
            sub-organization, untick Use existing policy.
          </>
        ) : (
          <>
            A new claim will be created based on claim {claim.claim_id_display}. On the next screen, select the policy
            the claim should have been created under — the new claim will be created under that policy&apos;s line of
            business and sub-organization, and both claims will be linked to each other.
          </>
        )}
      </Text>
      {!isSourceClosed && (
        <RadioGroup
          className="mt-16 flex flex-col"
          aria-label="Original claim"
          value={isCloseOutSelected ? 'close' : 'keep'}
          onChange={(_event, value) => setShouldCloseOriginal(value === 'close')}
        >
          <Tooltip title={closeDisabledReasons.join(', ')} disabled={canCloseOriginal}>
            {/* Tooltip clones its child with hover handlers, which the Atomic FormControlLabel
                rejects as unexpected props, so the handlers land on a wrapper instead. */}
            <span>
              <FormControlLabel
                disabled={!canCloseOriginal}
                control={<Radio value="close" />}
                label={`Close claim ${claim.claim_id_display} before creating the new claim`}
              />
            </span>
          </Tooltip>
          {isConfigurableInlineReasonShown && (
            <div className="ml-32 mt-8">
              <Formik
                innerRef={configurableCloseFormRef}
                initialValues={CLAIM_CLOSING_REASON_INITIAL_VALUES}
                validationSchema={Yup.object().shape(getClaimClosingReasonValidationSchema(closingReasons.reasonDict))}
                onSubmit={(values: ClaimClosingReasonValues) => submitConfigurableClose(values)}
              >
                <ClaimClosingReasonFields
                  reasonDict={closingReasons.reasonDict}
                  isLoading={closingReasons.isLoading}
                  isError={closingReasons.isError}
                />
              </Formik>
            </div>
          )}
          {isLegacyInlineReasonShown && (
            <div className="ml-32 mt-8 flex flex-col gap-8">
              <SingleSelectField<string>
                id="create-like-close-reason"
                label="Reason"
                value={selectedCloseTypeKey}
                onChange={(value) => setSelectedCloseTypeKey(value)}
                autoSelectSingleOption={false}
                fullWidth
                disabled={isSubmitting}
              >
                {closeTypes.map((closeType) => (
                  <MenuItem key={closeType.key} value={closeType.key}>
                    <span dangerouslySetInnerHTML={{ __html: closeType.desc }} />
                  </MenuItem>
                ))}
              </SingleSelectField>
              {!!selectedCloseType?.note && (
                <Text
                  variant={Text.VARIANTS.XS}
                  weight={Text.WEIGHTS.REGULAR}
                  colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                >
                  Closing note: {selectedCloseType.note}
                </Text>
              )}
            </div>
          )}
          <FormControlLabel control={<Radio value="keep" />} label={`Keep claim ${claim.claim_id_display} open`} />
        </RadioGroup>
      )}
      <div className="mt-16 flex flex-col">
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
          Policy of the new claim
        </Text>
        <Tooltip title={existingPolicyDisabledReason} disabled={canUseExistingPolicy}>
          {/* A wrapper for the tooltip's hover handlers, as on the close radio above. */}
          <span>
            <FormControlLabel
              disabled={!canUseExistingPolicy}
              control={
                <Checkbox
                  checked={isExistingPolicySelected}
                  onChange={(event) => setUseExistingPolicy(event.target.checked)}
                />
              }
              label="Use existing policy"
            />
          </span>
        </Tooltip>
      </div>
      <div className="mt-16 flex flex-col">
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
          Carry over to the new claim
        </Text>
        <FormControlLabel
          control={<Checkbox checked={copyDocuments} onChange={(event) => setCopyDocuments(event.target.checked)} />}
          label="Copy documents"
        />
        <FormControlLabel
          control={<Checkbox checked={copyNotes} onChange={(event) => setCopyNotes(event.target.checked)} />}
          label="Copy notes"
        />
        <FormControlLabel
          control={
            <Checkbox checked={copyCommunications} onChange={(event) => setCopyCommunications(event.target.checked)} />
          }
          label="Copy communications"
        />
        <FormControlLabel
          control={<Checkbox checked={copyContacts} onChange={(event) => setCopyContacts(event.target.checked)} />}
          label="Copy contacts"
        />
        {hasExternalClaimNumbers && (
          <FormControlLabel
            control={
              <Checkbox
                checked={copyExternalClaimNumbers}
                onChange={(event) => setCopyExternalClaimNumbers(event.target.checked)}
              />
            }
            label="Copy external claim numbers"
          />
        )}
        {hasCarriableLegalActions && (
          <FormControlLabel
            control={
              <Checkbox checked={copyLegalActions} onChange={(event) => setCopyLegalActions(event.target.checked)} />
            }
            label="Copy legal actions"
          />
        )}
        {legalActionsNotCopied && (
          <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {legalActionsNotCopied}
          </Text>
        )}
        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          All text notes are copied, including system notes. Copied documents are listed in a single note on the new
          claim. Copied communications keep their original dates and direction. Contacts are linked to the new claim,
          not duplicated.
          {hasCarriableLegalActions &&
            ' Copied legal actions keep their parties, dates of service and dispute resolution professionals; their' +
              ' logs, due dates and linked documents are not copied.'}
        </Text>
      </div>
    </CardDialog>
  );
};

export default CreateLikeClaimDialog;
